import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Check } from "lucide-react";
import { toast } from "sonner";

const DURATIONS = [
  { value: "monthly", label: "Monthly (30 days)", months: 1 },
  { value: "quarterly", label: "Quarterly (90 days)", months: 3 },
  { value: "half_yearly", label: "Half-yearly (180 days)", months: 6 },
  { value: "yearly", label: "Yearly (365 days)", months: 12 },
];

const MODULE_OPTIONS = [
  "patients", "opd", "optometry", "appointments", "ot", "ipd", "lab",
  "pharmacy", "billing", "panel", "abdm", "reports", "masters",
];

const EMPTY = {
  name: "", code: "", description: "", duration: "yearly",
  branch_limit: 1, user_limit: 10, storage_limit_gb: 2,
  price_inr: 0, mrp_inr: 0, validity_months: 12, modules: ["opd", "billing", "reports"],
  features: [], is_active: true, is_recommended: false,
  trial_days: 0, is_trial: false, sort_order: 100,
};

export default function SuperAdminPlans() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [confirmDel, setConfirmDel] = useState(null);
  const [featureInput, setFeatureInput] = useState("");

  const load = async () => { const { data } = await api.get("/superadmin/plans"); setItems(data.items); };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(s => ({ ...s, [k]: v }));
  const openCreate = () => { setEditing(null); setForm(EMPTY); setFeatureInput(""); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "", code: p.code || "", description: p.description || "",
      duration: p.duration || "yearly",
      branch_limit: p.branch_limit || 1, user_limit: p.user_limit || 10,
      storage_limit_gb: p.storage_limit_gb || 2, price_inr: p.price_inr || 0,
      mrp_inr: p.mrp_inr || 0,
      is_recommended: p.is_recommended || false,
      validity_months: p.validity_months || 12,
      modules: p.modules || [], features: p.features || [],
      is_active: p.is_active !== false,
      trial_days: p.trial_days || 0, is_trial: p.is_trial || false,
      sort_order: p.sort_order ?? 100,
    });
    setFeatureInput("");
    setOpen(true);
  };

  const onDuration = (v) => {
    const d = DURATIONS.find(x => x.value === v);
    setForm(s => ({ ...s, duration: v, validity_months: d?.months || s.validity_months }));
  };
  const toggleModule = (m) => setForm(s => ({
    ...s, modules: s.modules.includes(m) ? s.modules.filter(x => x !== m) : [...s.modules, m]
  }));
  const addFeature = () => {
    const v = featureInput.trim();
    if (!v) return;
    setForm(s => ({ ...s, features: [...(s.features || []), v] }));
    setFeatureInput("");
  };
  const removeFeature = (i) => setForm(s => ({ ...s, features: s.features.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.name?.trim()) { toast.error("Plan name is required"); return; }
    try {
      if (editing) {
        await api.patch(`/superadmin/plans/${editing.id}`, form);
        toast.success("Plan updated");
      } else {
        await api.post("/superadmin/plans", form);
        toast.success("Plan created");
      }
      setOpen(false); setForm(EMPTY); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };
  const remove = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/superadmin/plans/${confirmDel.id}`);
      toast.success("Plan deleted");
      setConfirmDel(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="superadmin-plans-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="section-label">SaaS Console</div>
          <h1 className="font-heading text-3xl font-semibold">Subscription plans</h1>
          <p className="mt-1 text-sm text-ink-muted">{items.length} plan{items.length === 1 ? "" : "s"} configured</p>
        </div>
        <Button onClick={openCreate} className="bg-brand hover:bg-brand-700" data-testid="plan-new-btn">
          <Plus size={14} className="mr-2" />Add plan
        </Button>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/60 text-left text-[11px] uppercase tracking-wider text-ink-muted">
            <tr>
              <th className="px-4 py-3">Name / Code</th>
              <th className="px-4 py-3">Duration</th>
              <th className="px-4 py-3 text-right">Price</th>
              <th className="px-4 py-3 text-right">Branches</th>
              <th className="px-4 py-3 text-right">Users</th>
              <th className="px-4 py-3 text-right">Storage</th>
              <th className="px-4 py-3">Modules</th>
              <th className="px-4 py-3 text-center">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items.map(p => (
              <tr key={p.id} className="hover:bg-secondary/30" data-testid={`plan-row-${p.id.slice(0, 6)}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-brand flex items-center gap-2">
                    {p.name}
                    {p.is_trial && p.trial_days > 0 && <Badge className="bg-emerald-600 hover:bg-emerald-700 text-[10px]">FREE · {p.trial_days}d</Badge>}
                  </div>
                  {p.code && <div className="text-[11px] text-ink-muted font-mono">{p.code}</div>}
                </td>
                <td className="px-4 py-3 capitalize">{(p.duration || "yearly").replace("_", "-")}</td>
                <td className="px-4 py-3 text-right font-medium">
                  <div>₹ {Number(p.price_inr).toLocaleString("en-IN")}</div>
                  {p.mrp_inr > 0 && p.mrp_inr > p.price_inr && (
                    <div className="text-[10px] font-normal text-ink-muted">
                      <span className="line-through">₹{Number(p.mrp_inr).toLocaleString("en-IN")}</span>
                      <span className="ml-1 text-emerald-700">−{Math.round(((p.mrp_inr - p.price_inr) / p.mrp_inr) * 100)}%</span>
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right">{p.branch_limit}</td>
                <td className="px-4 py-3 text-right">{p.user_limit}</td>
                <td className="px-4 py-3 text-right">{p.storage_limit_gb} GB</td>
                <td className="px-4 py-3"><span className="text-xs text-ink-muted">{(p.modules || []).length} modules</span></td>
                <td className="px-4 py-3 text-center">
                  {p.is_active !== false
                    ? <Badge variant="outline" className="border-emerald-300 bg-emerald-50 text-emerald-700">Active</Badge>
                    : <Badge variant="outline" className="border-zinc-300 bg-zinc-100 text-zinc-600">Inactive</Badge>}
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`plan-edit-${p.id.slice(0,6)}`}><Pencil size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmDel(p)} data-testid={`plan-delete-${p.id.slice(0,6)}`}><Trash2 size={14} className="text-rose-500" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-sm text-ink-muted">No plans yet.</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit plan" : "New subscription plan"}</DialogTitle></DialogHeader>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="md:col-span-2">
              <Label>Plan name *</Label>
              <Input value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Growth Annual" data-testid="plan-form-name" />
            </div>
            <div>
              <Label>Short code</Label>
              <Input value={form.code} onChange={e => set("code", e.target.value)} placeholder="GROW-A" data-testid="plan-form-code" />
            </div>
            <div>
              <Label>Billing cycle *</Label>
              <select className="w-full rounded-md border bg-background px-3 py-2 text-sm" value={form.duration} onChange={e => onDuration(e.target.value)} data-testid="plan-form-duration">
                {DURATIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Textarea rows={2} value={form.description} onChange={e => set("description", e.target.value)} placeholder="Short pitch (visible to hospital admins)" />
            </div>
            <div>
              <Label>MRP (₹)</Label>
              <Input type="number" value={form.mrp_inr} onChange={e => set("mrp_inr", Number(e.target.value))} placeholder="e.g. 5988" data-testid="plan-form-mrp" />
              <div className="mt-1 text-[10px] text-ink-muted">Actual price shown crossed-out. Leave 0 to hide.</div>
            </div>
            <div>
              <Label>Price (₹)</Label>
              <Input type="number" value={form.price_inr} onChange={e => set("price_inr", Number(e.target.value))} data-testid="plan-form-price" />
              {form.mrp_inr > 0 && form.price_inr > 0 && form.mrp_inr > form.price_inr && (
                <div className="mt-1 text-[10px] font-semibold text-emerald-700" data-testid="plan-form-discount-preview">
                  {Math.round(((form.mrp_inr - form.price_inr) / form.mrp_inr) * 100)}% off
                </div>
              )}
            </div>
            <div><Label>Validity (months)</Label><Input type="number" value={form.validity_months} onChange={e => set("validity_months", Number(e.target.value))} /></div>
            <div><Label>Max branches</Label><Input type="number" value={form.branch_limit} onChange={e => set("branch_limit", Number(e.target.value))} data-testid="plan-form-branches" /></div>
            <div><Label>Max users</Label><Input type="number" value={form.user_limit} onChange={e => set("user_limit", Number(e.target.value))} data-testid="plan-form-users" /></div>
            <div className="md:col-span-2"><Label>Storage (GB)</Label><Input type="number" value={form.storage_limit_gb} onChange={e => set("storage_limit_gb", Number(e.target.value))} /></div>

            <div className="md:col-span-2">
              <Label className="mb-2 block">Modules included</Label>
              <div className="flex flex-wrap gap-2">
                {MODULE_OPTIONS.map(m => {
                  const on = form.modules.includes(m);
                  return (
                    <button key={m} type="button" onClick={() => toggleModule(m)}
                      data-testid={`plan-module-${m}`}
                      className={`rounded-full border px-3 py-1 text-xs capitalize transition ${on ? "border-brand bg-brand text-white" : "border-border bg-background text-ink-muted hover:bg-secondary"}`}>
                      {on && <Check size={11} className="mr-1 inline" />}{m}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="md:col-span-2">
              <Label className="mb-2 block">Feature bullets (shown to hospitals)</Label>
              <div className="flex gap-2">
                <Input value={featureInput} onChange={e => setFeatureInput(e.target.value)} placeholder="e.g. Priority email support"
                  onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addFeature(); } }} />
                <Button type="button" variant="outline" onClick={addFeature}>Add</Button>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {(form.features || []).map((f, i) => (
                  <Badge key={i} variant="outline" className="cursor-pointer" onClick={() => removeFeature(i)}>
                    {f} ✕
                  </Badge>
                ))}
              </div>
            </div>

            <div className="md:col-span-2 flex flex-wrap items-center gap-x-6 gap-y-2 pt-2">
              <div className="flex items-center gap-2">
                <Switch checked={form.is_active} onCheckedChange={(v) => set("is_active", v)} data-testid="plan-form-active" />
                <Label>Plan is active (hospitals can subscribe)</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={form.is_recommended} onCheckedChange={(v) => set("is_recommended", v)} data-testid="plan-form-recommended" />
                <Label>Show <span className="rounded-full bg-gradient-to-r from-amber-500 to-orange-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">★ Best for you</span> ribbon</Label>
              </div>
            </div>

            <div className="md:col-span-2 rounded-lg border-2 border-emerald-200 bg-emerald-50/40 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Switch checked={form.is_trial} onCheckedChange={(v) => set("is_trial", v)} data-testid="plan-form-istrial" />
                <Label className="text-base">Free Trial plan</Label>
                <span className="text-xs text-emerald-700">Hospitals can claim this once without payment</span>
              </div>
              {form.is_trial && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Trial duration (days)</Label>
                    <Input type="number" value={form.trial_days} onChange={e => set("trial_days", Number(e.target.value))} placeholder="30" data-testid="plan-form-trialdays" />
                    <p className="text-[11px] text-ink-muted mt-1">Super admin can change this anytime. Hospitals get this many days free.</p>
                  </div>
                  <div>
                    <Label>Display order</Label>
                    <Input type="number" value={form.sort_order} onChange={e => set("sort_order", Number(e.target.value))} placeholder="10" />
                    <p className="text-[11px] text-ink-muted mt-1">Lower = shown first. Trial plans typically use 10.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          <Button onClick={save} className="mt-3 bg-brand hover:bg-brand-700" data-testid="plan-save-btn">
            {editing ? "Save changes" : "Create plan"}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-rose-700">Delete this plan?</DialogTitle></DialogHeader>
          <p className="text-sm">Permanently delete <b>{confirmDel?.name}</b>? Plans in use by hospitals cannot be deleted — re-assign them first.</p>
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConfirmDel(null)}>Cancel</Button>
            <Button onClick={remove} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="plan-delete-confirm">Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
